import { GetServerDataReturn, HeadProps, PageProps, graphql, navigate } from 'gatsby'
import React from 'react'
import { AppHead } from '../../../../components/common'
import { GoogleForJobs, GoogleForJobsMapper } from '../../../../components/offers/offer/googleForJobs'
import { Offer } from '../../../../components/offers/offer/mapper'
import OfferTemplate from '../../../../components/offers/offerTemplate'
import { convertStrapiOfferToOffer, fetchOneBySlug } from '../../../../services/offers/fetchOneBySlug.service'

interface ServerRequestProps {
  offer: Offer | null
  mappedForGoogleData: GoogleForJobs | null
}

const OfferPage: React.FC<PageProps<Queries.constantsQuery>> = ({
  data,
  serverData = { offer: null },
}) => {
  const constants = {
    siteStrapiUrl: data.site?.siteMetadata?.siteStrapiUrl ?? '',
    frontStrapiToken: data.site?.siteMetadata?.frontStrapiToken ?? '',
    googleMapsApiKey: data.site?.siteMetadata?.googleMaps?.apiKey || '',
    recaptchaKey: data.site?.siteMetadata?.recaptchaKey || '',
  }

  const { offer } = serverData as ServerRequestProps

  if (!offer) {
    if (typeof window !== 'undefined') {
      navigate('/404')
    }
    return null
  }

  return (
    <OfferTemplate
      data={offer}
      constants={constants}
      isCareerSite={false}
    />
  )
}

export default OfferPage

export async function getServerData({
  params,
}: {
  params: { slug: string }
}): GetServerDataReturn<ServerRequestProps> {
  try {
    const backEndUrl = process.env.CORE_STRAPI_URL
    const token = process.env.CORE_STRAPI_READ_TOKEN

    if (!backEndUrl) {
      throw new Error(`Strapi url is not defined GroupPage`)
    }

    if (!token) {
      throw new Error(`Strapi token is not defined GroupPage`)
    }

    const { slug } = params

    if (!slug) {
      throw new Error(`Slug is not defined OfferPage`)
    }

    const response = await fetchOneBySlug({
      slug,
      strapiUrl: backEndUrl,
      strapiToken: token,
    })

    if (!response) {
      return {
        status: 404,
        headers: {},
        props: { offer: null, mappedForGoogleData: null },
      }
    }

    const convertedOffer = convertStrapiOfferToOffer(response)

    return {
      props: {
        offer: convertedOffer,
        mappedForGoogleData: GoogleForJobsMapper(convertedOffer),
      },
    }
  } catch (error) {
    console.error(error, 'error')
    return {
      status: 500,
      headers: {},
      props: {
        offer: null,
        mappedForGoogleData: null,
      },
    }
  }
}

export const Head: React.FC<HeadProps<Queries.OfferQuery>> = ({ data, serverData }) => {
  const { offer, mappedForGoogleData } = serverData as ServerRequestProps

  if (!offer) {
    return null
  }

  const firm = offer.office?.firm

  const NoIndexingPage =
    firm?.name === 'Cabinet Lama & Associés'
  const offerFirmheaderImage =
    (firm?.header) as string ||
    '/lamacompta-sharing-default.jpg'
  const offerTitle = offer.title
  const offerCity = offer.office?.city
  const cityText = offerCity ? `à ${offerCity}` : ''
  const offerFirm = firm?.name
  const linkCanonical = offer.slug
    ? `${data.site?.siteMetadata?.siteUrl}/cabinets/${firm?.slug}/offres/${offer.slug}`
    : null

  return (
    <AppHead
      title={`${offerFirm} recrute un(e) ${offerTitle}`}
      metaDescription={`${offerFirm} recrute un(e) ${offerTitle} ${cityText}`}
      metaTitle={`${firm?.name} recrute un(e) ${offer.title} ${cityText}`}
      metaImage={offerFirmheaderImage}
      linkCanonical={linkCanonical}
      data={mappedForGoogleData || undefined}
      noIndexing={NoIndexingPage}
    />
  )
}


export const query = graphql`
  query constants {
    site {
      siteMetadata {
        siteStrapiUrl
        siteUrl
        frontStrapiToken
        recaptchaKey
        googleMaps {
          apiKey
        }
      }
    }
  }
`
